// ============================================================================
// MarketWizardSteps — шаги мастера заявки.
// Вынесены из страницы (SRP): страница управляет состоянием/отправкой, шаги —
// только разметкой и вводом. Пишут существующие реквизиты mkt_requests.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Camera, CheckCircle2, ImagePlus, MapPin, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MARKET_MAX_PHOTOS,
  MARKET_WIZARD_STEPS,
} from '@/lib/marketConstants';
import type { MarketCategory, MarketFailure, MarketPhoneState } from '@/lib/market';
import type { MarketGeoValue } from '@/lib/marketGeo';
import type { MarketSavedAddress } from '@/lib/marketProfile';
import { MarketError, MarketLoading } from '@/components/market/MarketState';
import { MarketGeoFields } from './MarketGeoFields';
import { MarketPhoneStatus } from './MarketPhoneStatus';

export function StepDots({ step }: { step: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {MARKET_WIZARD_STEPS.map((label, index) => {
        const n = index + 1;
        const done = n < step;
        const active = n === step;
        return (
          <div key={label} className="flex flex-1 items-center gap-1.5">
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                done
                  ? 'bg-primary text-primary-foreground'
                  : active
                    ? 'border-2 border-primary text-primary'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {done ? '✓' : n}
            </span>
            {index < MARKET_WIZARD_STEPS.length - 1 && (
              <span className={`h-0.5 flex-1 ${done ? 'bg-primary' : 'bg-muted'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Превью выбранного фото (локальный object URL, освобождается при удалении). */
export function LocalPhoto({ file, onRemove }: { file: File; onRemove: () => void }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
    <div className="relative aspect-square overflow-hidden rounded-xl border bg-muted">
      {url ? (
        <img src={url} alt={file.name} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full items-center justify-center text-muted-foreground">
          <ImagePlus className="h-5 w-5" />
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-background/90 text-foreground shadow"
        aria-label={`Убрать ${file.name}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ---------- Шаг 1: категория ---------------------------------------------------

export function MarketCategoryStep({
  categories,
  failure,
  categoryId,
  onSelect,
  onRetry,
}: {
  categories: MarketCategory[] | null;
  failure: MarketFailure | null;
  categoryId: string;
  onSelect: (id: string) => void;
  onRetry: () => void;
}) {
  const selectable = (categories || []).filter((c) => !c.isGroup);
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Что нужно сделать?</h2>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Выберите категорию услуги — так заявку быстрее увидят подходящие мастера.
        </p>
      </div>

      {failure ? (
        <MarketError failure={failure} onRetry={onRetry} />
      ) : categories === null ? (
        <MarketLoading text="Загружаем категории…" />
      ) : selectable.length === 0 ? (
        <div className="rounded-2xl border border-dashed bg-card/50 px-4 py-6 text-sm text-muted-foreground">
          В базе нет категорий услуг. Обратитесь к администратору — без категории заявку
          создать нельзя.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {selectable.map((category) => {
            const selected = category.id === categoryId;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => onSelect(category.id)}
                className={`flex min-h-[52px] items-center justify-between gap-2 rounded-2xl border px-4 py-3 text-left text-sm transition-colors ${
                  selected
                    ? 'border-primary bg-primary/10 font-semibold text-primary'
                    : 'bg-card hover:border-primary/40 active:bg-accent/40'
                }`}
              >
                <span>{category.name}</span>
                {selected && <CheckCircle2 className="h-4 w-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---------- Шаг 2: район и адрес ----------------------------------------------

export function MarketAddressStep({
  address,
  onChange,
  geo,
  onGeoChange,
  savedAddresses = [],
  onUseSavedAddress,
  saveAddress,
  onToggleSaveAddress,
}: {
  address: string;
  onChange: (value: string) => void;
  geo: MarketGeoValue;
  onGeoChange: (value: MarketGeoValue) => void;
  savedAddresses?: MarketSavedAddress[];
  onUseSavedAddress?: (address: MarketSavedAddress) => void;
  saveAddress?: boolean;
  onToggleSaveAddress?: (value: boolean) => void;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Где нужно выполнить работу?</h2>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Укажите район и адрес. Чем точнее адрес, тем быстрее мастер оценит задачу.
        </p>
      </div>

      {savedAddresses.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium">Мои адреса</div>
          <div className="space-y-1.5">
            {savedAddresses.map((saved) => {
              const full = [saved.city, saved.district, saved.address]
                .map((part) => part.trim())
                .filter(Boolean)
                .join(', ');
              return (
                <button
                  key={saved.id}
                  type="button"
                  onClick={() => onUseSavedAddress?.(saved)}
                  className="flex w-full items-start gap-2 rounded-2xl border bg-card px-3 py-2.5 text-left transition-colors hover:border-primary/40 active:bg-accent/40"
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5 text-xs font-medium">
                      {saved.label}
                      {saved.isDefault && (
                        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] text-primary">
                          основной
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block break-words text-[11px] leading-4 text-muted-foreground">
                      {full}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <textarea
        value={address}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        placeholder="Например: ул. Ленина, 10, кв. 25"
        className="w-full resize-none rounded-2xl border bg-background px-4 py-3 text-base leading-6 outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
      />

      <div className="rounded-2xl border bg-card p-3.5">
        <MarketGeoFields
          value={geo}
          onChange={onGeoChange}
          radiusLabel="Радиус поиска мастера, км"
          radiusHint="Насколько далеко от места работы искать мастеров. Пусто — без ограничения по расстоянию."
        />
        <p className="mt-2 text-[11px] leading-5 text-muted-foreground">
          Гео помогает показать заявку мастерам рядом. Адрес можно уточнить текстом —
          точные координаты и контакты мастер увидит только после взятия заявки.
        </p>
      </div>
      {onToggleSaveAddress && (
        <label className="flex items-center justify-between gap-2 rounded-xl border bg-card/60 px-3 py-2">
          <span className="min-w-0 flex-1 text-[11px] leading-4 text-muted-foreground">
            Сохранить адрес в «Мои адреса» — пригодится для следующих заявок.
          </span>
          <input
            type="checkbox"
            className="h-4 w-4 shrink-0"
            checked={saveAddress !== false}
            onChange={(e) => onToggleSaveAddress(e.target.checked)}
          />
        </label>
      )}
    </section>
  );
}

// ---------- Шаг 3: описание и фото ---------------------------------------------

export function MarketDescriptionStep({
  descr,
  onChange,
  photos,
  photoError,
  onAddPhotos,
  onRemovePhoto,
}: {
  descr: string;
  onChange: (value: string) => void;
  photos: File[];
  photoError: string;
  onAddPhotos: (files: File[]) => void;
  onRemovePhoto: (index: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Опишите задачу</h2>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Что случилось, что нужно сделать и на что обратить внимание. Фото помогут
          мастеру оценить объём работ.
        </p>
      </div>
      <textarea
        value={descr}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        placeholder="Например: протекает смеситель на кухне, нужна замена картриджа. Желательно сегодня после 18:00."
        className="w-full resize-none rounded-2xl border bg-background px-4 py-3 text-base leading-6 outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
      />
      <p className="text-[11px] leading-5 text-muted-foreground">
        Желаемую дату и время укажите в описании — отдельного поля в заявке пока нет.
      </p>

      <div className="rounded-2xl border p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-medium">Фото</div>
            <div className="text-[11px] text-muted-foreground">Необязательно, до {MARKET_MAX_PHOTOS} штук</div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 rounded-xl"
            onClick={() => inputRef.current?.click()}
            disabled={photos.length >= MARKET_MAX_PHOTOS}
          >
            <Camera className="mr-1.5 h-4 w-4" />
            Добавить
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              onAddPhotos(Array.from(e.target.files || []));
              e.target.value = '';
            }}
          />
        </div>

        {photoError && <div className="mt-2 text-xs text-destructive">{photoError}</div>}

        {photos.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            {photos.map((file, index) => (
              <LocalPhoto
                key={`${file.name}:${file.size}:${index}`}
                file={file}
                onRemove={() => onRemovePhoto(index)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- Шаг 4: телефон и проверка ------------------------------------------

export function MarketPhoneStep({
  phone,
  onChange,
  categoryName,
  address,
  descr,
  photoCount,
  offline,
  phoneGate,
  profilePhone,
}: {
  phone: string;
  onChange: (value: string) => void;
  categoryName: string;
  address: string;
  descr: string;
  photoCount: number;
  offline: boolean;
  /** Серверный статус телефона (гейт заявки) — не выдумываем локально. */
  phoneGate?: { state: MarketPhoneState; hint: string } | null;
  /** Телефон из профиля: несовпадение = заявка будет неподтверждённой. */
  profilePhone?: string;
}) {
  const digits = (value: string) => value.replace(/\D/g, '');
  const matchesProfile = !profilePhone?.trim()
    || !phone.trim()
    || digits(phone) === digits(profilePhone);
  // Гейт проверяет ТЕЛЕФОН ЗАЯВКИ против подтверждённого номера профиля:
  // другой номер при подтверждённом профиле всё равно даёт «не подтверждён».
  const state: MarketPhoneState | null = phone.trim()
    ? (!matchesProfile ? 'unverified' : phoneGate?.state || 'unknown')
    : null;
  const hint = !matchesProfile
    ? 'Номер отличается от телефона в профиле. Подтвердите именно этот номер — иначе мастера не смогут взять заявку.'
    : phoneGate?.hint;
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Как с вами связаться?</h2>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Телефон увидит мастер, который возьмёт заявку.
        </p>
      </div>
      <Input
        value={phone}
        onChange={(e) => onChange(e.target.value)}
        type="tel"
        inputMode="tel"
        placeholder="+7 900 000-00-00"
        className="h-12 rounded-2xl text-base"
        autoComplete="tel"
      />

      {state && (
        <MarketPhoneStatus
          state={state}
          audience="client"
          hint={hint || undefined}
        />
      )}

      <div className="rounded-2xl border bg-card p-4">
        <div className="text-sm font-semibold">Проверьте заявку</div>
        <dl className="mt-3 space-y-2.5 text-xs">
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-muted-foreground">Категория</dt>
            <dd className="min-w-0 flex-1">{categoryName || '—'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-muted-foreground">Адрес</dt>
            <dd className="min-w-0 flex-1 whitespace-pre-wrap break-words">{address.trim() || '—'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-muted-foreground">Описание</dt>
            <dd className="min-w-0 flex-1 whitespace-pre-wrap break-words">{descr.trim() || '—'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-muted-foreground">Фото</dt>
            <dd className="min-w-0 flex-1">{photoCount > 0 ? `${photoCount} шт.` : 'нет'}</dd>
          </div>
        </dl>
        {offline && (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-[11px] leading-5 text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Нет связи: заявка сохранится на устройстве и отправится автоматически.
              {photoCount > 0 ? ' Фото к такой заявке пока не прикрепить.' : ''}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
